// Tab "Lịch sử giao dịch": sổ hợp nhất hóa đơn POS, thanh toán / thu nợ / hoàn tiền, tiền thẻ, trừ buổi, gia hạn – lọc, tổng hợp, xuất Excel
import { useMemo, useState } from 'react';
import { DatePicker, Segmented, Select, Table } from 'antd';
import type { Dayjs } from 'dayjs';
import { Download } from 'lucide-react';
import { BRANCHES, METHOD_LABEL, branchName } from '../POS/data';
import { exportExcel } from '../POS/utils';
import { useTreatmentAuth } from '../Treatment/actions';
import { Kpi, Pill, fmtDateTime, money } from '../Treatment/ui';
import { LEDGER_KIND, ledgerSummary, type LedgerKind, type LedgerRow } from './logic';
import { Btn, LinkBar, type C360Ops } from './ui360';
import type { C360 } from './useCustomer360';

const GROUPS: Record<string, { label: string; kinds: LedgerKind[] | null }> = {
  all: { label: 'Tất cả', kinds: null },
  money: { label: 'Thu / hoàn tiền', kinds: ['payment', 'debt', 'refund', 'card_payment', 'card_refund'] },
  bill: { label: 'Hóa đơn', kinds: ['order', 'card_import'] },
  treatment: { label: 'Liệu trình', kinds: ['use', 'extend', 'adjust', 'card_import', 'card_payment', 'card_refund'] },
};

const STATUS_TEXT: Record<string, string> = {
  completed: 'Hoàn tất', debt: 'Còn nợ', partially_refunded: 'Hoàn một phần', refunded: 'Đã hoàn', cancelled: 'Đã hủy',
  pending: 'Chờ duyệt', approved: 'Đã duyệt', paid: 'Đã chi', rejected: 'Từ chối',
};

export default function LedgerPanel({ c, ops }: { c: C360; ops: C360Ops }) {
  const { can } = useTreatmentAuth();
  const [group, setGroup] = useState('all');
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [branch, setBranch] = useState('');

  const rows = useMemo(() => {
    const kinds = GROUPS[group].kinds;
    const from = range?.[0]?.startOf('day').toISOString();
    const to = range?.[1]?.endOf('day').toISOString();
    return c.ledger.filter(r => (!kinds || kinds.includes(r.kind)) && (!branch || r.branchId === branch) && (!from || r.at >= from) && (!to || r.at <= to));
  }, [c.ledger, group, range, branch]);
  const s = ledgerSummary(rows);

  const exportRows = () => exportExcel(`giao-dich-${c.party.name || c.key}.xlsx`, 'Giao dịch', rows.map(r => ({
    'Thời gian': fmtDateTime(r.at), 'Loại': LEDGER_KIND[r.kind].label, 'Chứng từ': r.ref, 'Nội dung': r.title, 'Chi tiết': r.detail,
    'Chi nhánh': branchName(r.branchId), 'Giá trị': r.value, 'Tiền thu (+) / hoàn (−)': r.cash, 'Phương thức': r.method ? METHOD_LABEL[r.method] : '',
    'Người thực hiện': r.by, 'Trạng thái': r.status ? STATUS_TEXT[r.status] || r.status : '',
  })));

  return (
    <div className="space-y-4">
      <LinkBar c={c} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Tổng mua" value={`${money(s.billed)}đ`} sub="Hóa đơn + thẻ chuyển đổi" />
        <Kpi label="Đã thanh toán" value={`${money(s.paid)}đ`} tone="ok" />
        <Kpi label="Đã hoàn" value={`${money(s.refunded)}đ`} tone={s.refunded ? 'warn' : 'default'} />
        <Kpi label="Giá trị buổi đã dùng" value={`${money(s.used)}đ`} sub={`Còn nợ hiện tại ${money(c.metrics.debt)}đ`} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Segmented value={group} onChange={v => setGroup(v as string)} options={Object.entries(GROUPS).map(([value, g]) => ({ value, label: g.label }))} />
        <DatePicker.RangePicker format="DD/MM/YYYY" value={range} onChange={v => setRange(v as typeof range)} placeholder={['Từ ngày', 'Đến ngày']} />
        <Select className="w-40" value={branch} onChange={setBranch} options={[{ value: '', label: 'Mọi chi nhánh' }, ...BRANCHES.map(b => ({ value: b.id, label: b.name }))]} />
        {can('export') && <div className="ml-auto"><Btn onClick={exportRows} disabled={!rows.length}><Download className="h-4 w-4" /> Xuất Excel</Btn></div>}
      </div>
      <Table<LedgerRow> rowKey="id" size="middle" className="custom-ant-table" dataSource={rows} pagination={{ pageSize: 15, hideOnSinglePage: true }} scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'Chưa có giao dịch' }}
        onRow={r => ({ onClick: () => (r.link.type === 'order' ? ops.openOrder(r.link.id) : ops.openCard(r.link.id)), className: 'cursor-pointer' })}
        columns={[
          { title: 'Thời gian', width: 130, render: (_, r) => <span className="whitespace-nowrap">{fmtDateTime(r.at)}</span> },
          { title: 'Loại', width: 120, render: (_, r) => <Pill {...LEDGER_KIND[r.kind]} /> },
          { title: 'Nội dung', render: (_, r) => <span className={r.status === 'cancelled' ? 'text-slate-400 line-through' : ''}>{r.title}<div className="text-xs text-slate-400"><span className="font-mono">{r.ref}</span>{r.detail ? ` · ${r.detail}` : ''}</div></span> },
          { title: 'Chi nhánh', width: 120, render: (_, r) => <span>{branchName(r.branchId)}<div className="text-xs text-slate-400">{r.by}</div></span> },
          { title: 'Giá trị', width: 110, align: 'right', render: (_, r) => (r.value ? money(r.value) : '—') },
          {
            title: 'Tiền thu / hoàn', width: 140, align: 'right', render: (_, r) => r.cash
              ? <span className={`font-semibold ${r.cash > 0 ? 'text-green-700' : 'text-violet-700'}`}>{r.cash > 0 ? '+' : '−'}{money(Math.abs(r.cash))}<div className="text-xs font-normal text-slate-400">{r.method ? METHOD_LABEL[r.method] : ''}</div></span>
              : r.status ? <span className="text-xs text-slate-500">{STATUS_TEXT[r.status] || r.status}</span> : '',
          },
        ]} />
    </div>
  );
}
